import React, { useEffect, useState } from "react";

import { openapiApi } from "../../api/client";
import { Badge, Button, Input, Label, Modal, Select } from "../../components/ui";
import { COLORS } from "../../theme/colors";
import { OPENAPI_TEMPLATES, deriveCredentialRef, templateFetchTarget } from "./openApiTemplates";

/**
 * OpenAPI Integrations tab — 5-step admin wizard (v2) that registers
 * any REST API as a tenant-scoped MCP integration via agentgateway.
 *
 * Steps:
 *   0. Template picker — pre-baked configs for ~10 common APIs OR
 *      "Start from scratch" (paste-spec / fetch-URL paths preserved
 *      from v1 — templates never gate the from-scratch flow).
 *   1. Spec source — URL or paste (skipped when a template was picked).
 *   2. Operation picker — pick which endpoints to expose.
 *   3. Auth + inline credential entry — paste API key / token directly;
 *      the server seeds tenant_settings under the auto-derived
 *      credential_ref via save_settings + encrypts at rest.
 *   4. Review + Test connection — synthetic upstream probe BEFORE
 *      persisting (no half-broken rows on a typo) → Create.
 *
 * Design: Notes/openapi-connector-design-v1.md §4 (v1 substrate);
 * v2 priorities (templates / inline credentials / test-before-create
 * / auth presets) tracked under §19 patch K-* follow-ups.
 */

const AUTH_METHODS = [
  { value: "none", label: "None (public API)" },
  { value: "api_key_header", label: "API key (header)" },
  { value: "api_key_query", label: "API key (query parameter)" },
  { value: "bearer", label: "Bearer token (long-lived)" },
  { value: "bearer_refresh", label: "Bearer + refresh token (auto-refresh)" },
  { value: "basic", label: "Basic auth" },
  { value: "oauth2_client_credentials", label: "OAuth2 client credentials" },
  { value: "oauth2_authorization_code", label: "OAuth2 authorization code (interactive consent)" },
];

// Per-auth-method conventional credential field name. Used to
// auto-derive credential_ref ("openapi.<slug>.<field>") and to
// title the inline credential value field.
const AUTH_FIELD_LABELS = {
  api_key_header: { field: "api_key",     label: "API key" },
  api_key_query:  { field: "api_key",     label: "API key" },
  bearer:         { field: "token",       label: "Bearer token" },
  bearer_refresh: { field: "refresh_token", label: "Refresh token (long-lived)" },
  basic:          { field: "basic_auth",  label: "Username:password" },
  oauth2_client_credentials: { field: "client_secret", label: "Client secret" },
  // authz_code's credential_ref points at refresh_token — the value
  // is filled in by /oauth/callback after the user approves consent;
  // there's no manual paste field on Step 3 (instead, Step 4 shows
  // a "Connect" button that drives the OAuth dance).
  oauth2_authorization_code: { field: "refresh_token", label: "Refresh token (set by Connect flow)" },
};

const TOTAL_STEPS = 5;

// Exported for unit testing — the wizard "Next"/"Create" gating rule.
//   0 = template picker — advances by clicking a tile (no Next btn)
//   1 = spec source     — advances via "Fetch spec → Next"
//   2 = operations      — Next when ≥1 op selected
//   3 = auth + naming   — name/slug/base_url all set + credential satisfied.
//                         authz-code needs authorize_url/token_url/client_id up
//                         front (the refresh_token arrives post-callback), so
//                         it's exempt from the inline-credential requirement.
//   4 = review          — always advanceable (Create).
export function computeCanAdvance(step, form) {
  const authNeedsCred = form.auth_method !== "none";
  const isAuthzCode = form.auth_method === "oauth2_authorization_code";
  const credSatisfied =
    !authNeedsCred || isAuthzCode || !!form.credential_value || !!form.credential_ref;
  const authzCodeReady =
    !isAuthzCode ||
    (!!form.auth_config?.authorize_url &&
      !!form.auth_config?.token_url &&
      !!form.auth_config?.client_id);
  if (step === 0) return false;
  if (step === 1) return false; // advances via Fetch spec
  if (step === 2) return form.enabled_operations.length > 0;
  if (step === 3) return !!(form.name && form.slug && form.base_url && credSatisfied && authzCodeReady);
  return true;
}

const EMPTY_FORM = {
  name: "",
  slug: "",
  base_url: "",
  auth_method: "bearer",
  credential_ref: "",
  credential_value: "",          // wizard v2: inline secret
  credential_label: "",          // hint from template (overrides default)
  credential_label_helper: "",   // template-specific helper text
  credential_help_url: "",       // "Where do I get this?" deep link
  auth_config: {},
  spec: null,
  spec_source_url: "",
  enabled_operations: [],
  template_id: null,             // null = from-scratch path
};

export default function OpenApiIntegrationsTab() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [previewing, setPreviewing] = useState(false);
  const [previewOps, setPreviewOps] = useState([]);
  const [specInput, setSpecInput] = useState({ mode: "obtain", url: "", text: "", hint: "" });
  const [submitting, setSubmitting] = useState(false);
  // Wizard v2: synthetic upstream probe state for the Test-connection
  // button on Step 4 (review). null = idle; object = last result.
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Item 8: OAuth callback status banner. After /oauth/callback redirects
  // back to /admin/openapi-integrations/<id>?oauth=connected|expired|...,
  // we read the query string once on mount, render the banner, then
  // strip the params from the URL so a refresh doesn't re-show.
  const [oauthStatus, setOauthStatus] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("oauth");
    if (status) {
      setOauthStatus({ status, detail: params.get("detail") });
      // Clean URL — keep the integration ID path but drop the OAuth qs
      params.delete("oauth");
      params.delete("detail");
      const remaining = params.toString();
      window.history.replaceState(
        {}, "",
        window.location.pathname + (remaining ? "?" + remaining : ""),
      );
    }
  }, []);

  const resetWizard = () => {
    setShowWizard(false);
    setStep(0);
    setForm(EMPTY_FORM);
    setPreviewOps([]);
    setSpecInput({ mode: "obtain", url: "", text: "", hint: "" });
    setTesting(false);
    setTestResult(null);
    setError("");
  };

  /**
   * Wizard v2 — Step 0: a template was picked. Pre-fill auth/name/etc.
   * from the template, then run obtain-spec against the template's
   * spec_url (preferred) or docs_url to load the FULL surface — not
   * a 1-op bundled lite spec.
   *
   * The fetch can take 5-30s for the docs path (LLM authoring) and is
   * shown via setPreviewing → spinner on the tile and a "Loading
   * <Provider> spec…" notice. On error the user lands back on Step 0
   * with a banner so they can retry or pick "Start from scratch".
   */
  async function applyTemplate(t) {
    // BYO-spec templates: pre-fill auth/name/base_url then route the
    // user to Step 1 (spec source) so they paste their org's
    // instance-specific Swagger. ServiceNow / Salesforce are the
    // canonical cases — every customer org has a unique instance
    // and there's no public master spec.
    if (t.byo_spec) {
      setForm(f => ({
        ...f,
        template_id: t.id,
        name: t.name,
        slug: t.id,
        base_url: t.base_url,
        auth_method: t.auth_method,
        auth_config: { ...(t.auth_config || {}) },
        spec: null,
        spec_source_url: "",
        enabled_operations: [],
        credential_ref: deriveCredentialRef(t.id, t.credential_field),
        credential_value: "",
        credential_label: t.credential_label || "",
        credential_label_helper: t.credential_label_helper || "",
        credential_help_url: t.credential_help_url || "",
      }));
      // Default to paste mode — most BYO-spec users have the JSON in
      // hand. The strict-URL mode is still a click away if they have
      // an internal spec hosted at a URL.
      setSpecInput({ mode: "paste", url: "", text: "", hint: "" });
      const helper = t.byo_spec_helper
        ? `${t.name}: ${t.byo_spec_helper}`
        : `${t.name} requires a per-instance spec — paste your Swagger / OpenAPI here.`;
      setError(helper);
      setStep(1);
      return;
    }

    const target = templateFetchTarget(t);
    if (!target) {
      setError(`Template ${t.name} has neither spec_url nor docs_url — cannot fetch spec`);
      return;
    }

    setForm(f => ({
      ...f,
      template_id: t.id,
      name: t.name,
      slug: t.id,
      base_url: t.base_url,
      auth_method: t.auth_method,
      auth_config: { ...(t.auth_config || {}) },
      spec: null,
      spec_source_url: target.url,
      enabled_operations: [],
      credential_ref: deriveCredentialRef(t.id, t.credential_field),
      credential_value: "",
      credential_label: t.credential_label || "",
      credential_label_helper: t.credential_label_helper || "",
      credential_help_url: t.credential_help_url || "",
    }));
    setPreviewing(true);
    setError("");

    // Try spec_url first (Path A — fast, deterministic, no LLM cost).
    // If the published spec fails the platform's validator (deep $ref
    // composition, oversize, etc.) AND the template ALSO carries a
    // docs_url, fall back to Path C (LLM-authored from prose). Many
    // big providers (OpenAI, Stripe, GitHub) ship specs that exceed
    // the platform's strict ref-depth/size guards; the docs path is
    // the practical out for those.
    const attempts = [];
    if (t.spec_url) attempts.push({ url: t.spec_url, hint: null, kind: "spec" });
    if (t.docs_url) attempts.push({ url: t.docs_url, hint: t.docs_hint || null, kind: "docs" });

    let lastErr = null;
    let succeeded = false;
    for (const att of attempts) {
      try {
        const resp = await openapiApi.obtainSpec({
          source_url: att.url, hint: att.hint,
        });
        const data = resp?.data || {};
        const ops = data.operations || [];
        const sk = data.source_kind;
        setPreviewOps(ops);
        // Start with NO operations selected — admin picks the subset
        // they actually need. This is correct UX for two reasons:
        //   (1) Phase 3 §C1 caps integrations at 100 ops each. Starting
        //       with all 174 (Slack) or 600+ (Stripe/Twilio) selected
        //       and asking the admin to deselect down is backwards.
        //   (2) LLM tool-selection accuracy and agentgateway latency
        //       both improve with fewer enabled operations. The admin
        //       knows their use case; they should triage UP from zero.
        // The wizard's Step 2 picker shows totals + a "Select all (up
        // to 100)" button if they want to enable many quickly.
        setForm(f => ({
          ...f,
          spec: data.spec || null,
          spec_source_url: att.url,
          base_url: data.servers?.[0]?.url || f.base_url,
          enabled_operations: [],
        }));
        const tag = sk === "openapi3" ? "OpenAPI 3 parsed directly"
                  : sk === "swagger2_converted" ? "Swagger 2.0 converted to OpenAPI 3.0"
                  : sk === "llm_generated" ? "Spec authored from docs prose"
                  : "";
        const wcount = (data.warnings || []).length;
        const w = wcount ? ` (${wcount} warning${wcount > 1 ? "s" : ""})` : "";
        const fallbackNote = att.kind === "docs" && t.spec_url
          ? " (fell back to docs after spec_url failed validator)"
          : "";
        const capHint = ops.length > 100
          ? ` — ${ops.length} ops available; pick the ones you need (max 100/integration)`
          : ` — ${ops.length} operations available; select what you need`;
        if (tag) setError(`✓ ${tag}${w}${fallbackNote}${capHint}`);
        succeeded = true;
        setStep(2);
        break;
      } catch (e) {
        lastErr = e?.body?.detail || e?.message || `${att.url} failed`;
      }
    }

    if (!succeeded) {
      setError(lastErr || `Failed to fetch ${t.name} spec`);
    }
    setPreviewing(false);
  }

  function pickFromScratch() {
    setForm(f => ({ ...f, template_id: null }));
    setStep(1);
  }

  async function fetchIntegrations() {
    setLoading(true);
    try {
      const resp = await openapiApi.list();
      setIntegrations(resp?.data?.integrations || []);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchIntegrations(); }, []);

  async function runPreview() {
    setPreviewing(true);
    setError("");
    try {
      // Wizard v2 — two modes:
      //   "obtain": obtain-spec endpoint (smart-detect: any spec URL OR
      //             HTML docs page; backend picks parse / Swagger-2
      //             convert / LLM-author path automatically).
      //   "paste":  obtain-spec rejects pasted text without a source
      //             URL, so this still routes through the legacy
      //             preview-spec endpoint. Pasted specs are assumed
      //             valid OpenAPI 3.x (the user typed it).
      // The pre-fix "Strict OpenAPI URL" mode used preview-spec
      // directly with spec_url; pasting an HTML docs URL into it
      // crashed with "spec is neither valid JSON nor YAML" because
      // the parser tried YAML on the HTML body. Smart-fetch handles
      // both shapes uniformly so the strict mode was just a footgun.
      let resp;
      let sourceUrl = "";
      if (specInput.mode === "paste") {
        resp = await openapiApi.previewSpec({ spec: specInput.text });
      } else {
        resp = await openapiApi.obtainSpec({
          source_url: specInput.url,
          hint: specInput.hint || null,
        });
        sourceUrl = specInput.url;
      }
      const ops = resp?.data?.operations || [];
      setPreviewOps(ops);
      // Empty selection by default — admin triages UP. See applyTemplate.
      setForm(f => ({
        ...f,
        spec: resp?.data?.spec || null,
        spec_source_url: sourceUrl,
        base_url: f.base_url || (resp?.data?.servers?.[0]?.url || ""),
        name: f.name || (resp?.data?.info?.title || ""),
        enabled_operations: [],
      }));
      const sk = resp?.data?.source_kind;
      const warns = resp?.data?.warnings || [];
      const tag = sk === "openapi3" ? "OpenAPI 3 spec parsed directly"
                : sk === "swagger2_converted" ? "Swagger 2.0 converted to OpenAPI 3.0"
                : sk === "llm_generated" ? "Spec authored from docs prose"
                : "Spec loaded";
      const w = warns.length ? ` (${warns.length} warning${warns.length > 1 ? "s" : ""})` : "";
      const capHint = ops.length > 100
        ? ` — ${ops.length} ops available; pick the ones you need (max 100/integration)`
        : ` — ${ops.length} operations available; select what you need`;
      setError(`✓ ${tag}${w}${capHint}`);
      setStep(2);
    } catch (e) {
      setError(e?.body?.detail || e?.message || String(e));
    } finally {
      setPreviewing(false);
    }
  }

  /**
   * Wizard v2 — synthetic upstream probe. Calls /test-config with the
   * current form state (including the inline credential value), gets
   * back {ok, status, latency_ms, body_excerpt, error_type}. Result
   * renders inline on Step 4 so the admin sees if the config works
   * BEFORE clicking Create.
   */
  async function runTestConfig() {
    setTesting(true);
    setError("");
    setTestResult(null);
    try {
      const resp = await openapiApi.testConfig({
        base_url: form.base_url,
        auth_method: form.auth_method,
        auth_config: form.auth_config || {},
        credential_value: form.credential_value || null,
        spec: form.spec,
        enabled_operations: form.enabled_operations,
      });
      setTestResult(resp?.data || null);
    } catch (e) {
      setTestResult({
        ok: false,
        status: 0,
        latency_ms: 0,
        body_excerpt: e?.body?.detail || e?.message || String(e),
        error_type: "request_failed",
      });
    } finally {
      setTesting(false);
    }
  }

  async function submitCreate() {
    setSubmitting(true);
    setError("");
    try {
      // Wizard v2: derive credential_ref if the user didn't override
      // it. Convention: openapi.<slug>.<field> where field comes from
      // the auth_method's conventional name.
      const conventional = AUTH_FIELD_LABELS[form.auth_method]?.field;
      const credRef = form.credential_ref ||
        (conventional ? deriveCredentialRef(form.slug, conventional) : null);

      // For oauth2_authorization_code, the inline ``credential_value``
      // is the OAuth client_secret (NOT the refresh_token — that comes
      // from /oauth/callback after consent). Store the client_secret
      // under a separate path that the callback handler reads via
      // ``client_secret_ref``. credential_ref points at refresh_token,
      // which gets populated post-callback.
      const isAuthzCode = form.auth_method === "oauth2_authorization_code";
      let payload = {
        name: form.name,
        slug: form.slug,
        base_url: form.base_url,
        auth_method: form.auth_method,
        credential_ref: credRef,
        credential_value: form.credential_value || null,
        spec: form.spec,
        spec_source_url: form.spec_source_url || null,
        auth_config: form.auth_config || {},
        enabled_operations: form.enabled_operations,
        operation_overrides: {},
      };
      if (isAuthzCode) {
        // Stash client_secret separately under openapi.<slug>.client_secret
        // and tell the callback where to find it via client_secret_ref.
        // credential_value at the API level is what gets persisted —
        // we route it to client_secret here, NOT to credential_ref's
        // path (which is reserved for the refresh_token from callback).
        const safeSlug = form.slug.toLowerCase().replace(/-/g, "_");
        const secretRef = `openapi.${safeSlug}.client_secret`;
        payload.auth_config = {
          ...payload.auth_config,
          client_secret_ref: secretRef,
        };
        // Server seeds tenant_settings with `credential_value` under
        // `credential_ref` (per CreateIntegrationRequest). To put the
        // client_secret at openapi.<slug>.client_secret instead, swap
        // the ref for this initial seed.
        payload.credential_ref = secretRef;
      }

      const created = await openapiApi.create(payload);

      if (isAuthzCode) {
        // Now restore credential_ref to the refresh_token path so the
        // /oauth/callback writes there. We update the integration
        // record after creation since the API doesn't expose a way
        // to set credential_ref independently of credential_value.
        const safeSlug = form.slug.toLowerCase().replace(/-/g, "_");
        const refreshRef = `openapi.${safeSlug}.refresh_token`;
        await openapiApi.update(created.id, {
          credential_ref: refreshRef,
        });

        // Drive the OAuth Connect flow. /oauth/start returns the
        // provider's authorize URL; we redirect the browser top-level
        // (popup is blocked by Safari for non-user-gesture redirects,
        // and browsers preserve form-submit gestures cleanly through
        // location.assign). Browser completes consent at provider,
        // redirects back to /oauth/callback which then redirects to
        // /admin/openapi-integrations/<id>?oauth=connected — the tile
        // detail view we already render.
        try {
          const startResp = await openapiApi.oauthStart(created.id);
          window.location.href = startResp.authorize_url;
          return;  // browser navigates away; resetWizard happens on return
        } catch (e) {
          setError(
            "Integration created but OAuth start failed: " +
            (e?.body?.detail || e?.message || String(e)) +
            ". Use the Reconnect button on the tile to retry."
          );
        }
      }

      await fetchIntegrations();
      resetWizard();
    } catch (e) {
      setError(e?.body?.detail || e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this integration? agentgateway will stop exposing its tools.")) return;
    try {
      await openapiApi.delete(id);
      await fetchIntegrations();
    } catch (e) {
      setError(e?.body?.detail || e?.message || String(e));
    }
  }

  async function handleTest(id) {
    setError("");
    try {
      const r = await openapiApi.test(id);
      const d = r?.data || {};
      const ok = d.ok ? "✓" : "✗";
      const errType = d.error_type ? ` (${d.error_type})` : "";
      window.alert(
        `${ok} ${d.method} ${d.path} → HTTP ${d.status}${errType}\n\n` +
        `Latency: ${d.latency_ms}ms\n` +
        `Response excerpt:\n${(d.body_excerpt || "").slice(0, 200)}`
      );
    } catch (e) {
      setError(e?.body?.detail || e?.message || String(e));
    }
  }

  // Mid-scope refresh (Tier-2 #6): preview → diff modal → apply.
  // The dialog state holds {integrationId, integrationName, diff,
  // preview_token}. Accept-button on the modal calls refreshApply.
  const [refreshDialog, setRefreshDialog] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(null); // integrationId being previewed
  const [refreshApplying, setRefreshApplying] = useState(false);

  async function handleRefresh(id) {
    setError("");
    setRefreshLoading(id);
    try {
      const r = await openapiApi.refreshPreview(id);
      const d = r?.data || {};
      const integration = (integrations || []).find(it => it.id === id);
      // Fast path: no changes — just say so without the modal.
      const totalChanges =
        (d.added?.length || 0) + (d.removed?.length || 0) + (d.changed?.length || 0);
      if (totalChanges === 0) {
        window.alert(`Spec is up to date. ${d.unchanged_count || 0} operations unchanged.`);
        return;
      }
      setRefreshDialog({
        integrationId: id,
        integrationName: integration?.name || id,
        diff: d,
        preview_token: d.preview_token,
      });
    } catch (e) {
      setError(e?.body?.detail || e?.message || String(e));
    } finally {
      setRefreshLoading(null);
    }
  }

  async function handleRefreshApply() {
    if (!refreshDialog) return;
    setRefreshApplying(true);
    try {
      await openapiApi.refreshApply(refreshDialog.integrationId, refreshDialog.preview_token);
      setRefreshDialog(null);
      await fetchIntegrations();
    } catch (e) {
      setError(e?.body?.detail || e?.message || String(e));
    } finally {
      setRefreshApplying(false);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>
            OpenAPI Integrations
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
            Register any REST API via its OpenAPI spec. agentgateway exposes
            the selected operations as MCP tools — no custom code per provider.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={fetchIntegrations}>Refresh</Button>
          <Button onClick={() => setShowWizard(true)}>+ Add Integration</Button>
        </div>
      </div>

      {error && (
        <div style={{
          background: `${COLORS.pink}12`, border: `1px solid ${COLORS.pink}30`, borderRadius: 8,
          padding: "10px 14px", marginBottom: 16, fontSize: 12, color: COLORS.pink,
        }}>
          {error}
        </div>
      )}

      {oauthStatus && <OAuthCallbackBanner status={oauthStatus} onDismiss={() => setOauthStatus(null)} />}

      {loading ? (
        <div style={{ color: COLORS.textMuted, fontSize: 12 }}>Loading…</div>
      ) : integrations.length === 0 ? (
        <EmptyState onAdd={() => setShowWizard(true)} />
      ) : (
        <IntegrationList items={integrations} onDelete={handleDelete} onRefresh={handleRefresh} onTest={handleTest} refreshLoadingId={refreshLoading} />
      )}

      {refreshDialog && (
        <RefreshDiffModal
          dialog={refreshDialog}
          applying={refreshApplying}
          onCancel={() => setRefreshDialog(null)}
          onApply={handleRefreshApply}
        />
      )}

      <Modal
        open={showWizard}
        onClose={resetWizard}
        title={`Add OpenAPI Integration — Step ${step + 1} of ${TOTAL_STEPS}`}
        width={680}
        footer={
          <WizardFooter
            step={step}
            onBack={() => {
              // Going back from Step 2 (operations) lands you on
              // Step 0 (templates) when a template was used — Step 1
              // (spec source) was skipped. From-scratch users go to 1.
              if (step === 2 && form.template_id) setStep(0);
              else setStep(s => Math.max(0, s - 1));
            }}
            onNext={() => setStep(s => Math.min(TOTAL_STEPS - 1, s + 1))}
            onPreview={runPreview}
            onSubmit={submitCreate}
            onCancel={resetWizard}
            previewing={previewing}
            submitting={submitting}
            form={form}
          />
        }
      >
        {/* In-modal error banner. Without this the page-level banner
            (above the integrations list) is hidden behind the open
            modal — bug surfaced by a user typoing the URL into
            Smart-fetch and seeing a "silent failure". The string
            starting with "✓" is reused as a non-blocking info note
            (e.g. "OpenAPI 3 parsed directly — 19 ops loaded"), so
            color it green when prefixed; pink otherwise. */}
        {error && (
          <div
            data-testid="wizard-error-banner"
            style={{
              marginBottom: 14, padding: "10px 14px", borderRadius: 8,
              fontSize: 12,
              background: error.startsWith("✓") ? `${COLORS.green}12` : `${COLORS.pink}12`,
              border: `1px solid ${error.startsWith("✓") ? COLORS.green : COLORS.pink}30`,
              color: error.startsWith("✓") ? COLORS.green : COLORS.pink,
            }}
          >
            {error}
          </div>
        )}
        {step === 0 && (
          <Step0TemplatePicker
            templates={OPENAPI_TEMPLATES}
            onPick={applyTemplate}
            onFromScratch={pickFromScratch}
            loading={previewing}
          />
        )}
        {step === 1 && (
          <Step1SpecSource specInput={specInput} setSpecInput={setSpecInput} />
        )}
        {step === 2 && (
          <Step2Operations
            operations={previewOps}
            selected={form.enabled_operations}
            onToggle={id => setForm(f => ({
              ...f,
              enabled_operations: f.enabled_operations.includes(id)
                ? f.enabled_operations.filter(x => x !== id)
                : [...f.enabled_operations, id],
            }))}
            onSelectAll={() => setForm(f => ({
              // Cap "Select all" at the first 100 ops (Phase 3 §C1).
              // For specs > 100 ops admins should triage manually or
              // split into scoped integrations; clicking "Select all"
              // when 600 ops are available shouldn't enable 600.
              ...f, enabled_operations: previewOps.slice(0, 100).map(o => o.operationId),
            }))}
            onSelectNone={() => setForm(f => ({ ...f, enabled_operations: [] }))}
          />
        )}
        {step === 3 && (
          <Step3Auth form={form} setForm={setForm} />
        )}
        {step === 4 && (
          <Step4Review
            form={form}
            operationCount={form.enabled_operations.length}
            onTest={runTestConfig}
            testing={testing}
            testResult={testResult}
          />
        )}
      </Modal>
    </div>
  );
}


/**
 * Step 0 — Template picker.
 *
 * Two columns of cards: a tile for each pre-baked template + one
 * "Start from scratch" tile that takes the from-scratch path. Picking
 * a template fully fills the form (spec / auth / base_url / auth_config
 * / credential_ref + label hints) and skips Step 1 (spec source).
 */
function Step0TemplatePicker({ templates, onPick, onFromScratch, loading }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: 12, color: COLORS.textMuted }}>
        Pick a template to pre-fill auth + base URL and auto-fetch the full
        spec from the provider — no 1-op demo. Or start from scratch with any
        OpenAPI / Swagger / docs URL.
      </div>
      {loading && (
        <div style={{
          padding: "8px 12px", borderRadius: 8,
          background: `${COLORS.accent}10`,
          border: `1px solid ${COLORS.accent}40`,
          fontSize: 12, color: COLORS.accentText || COLORS.accent,
        }}>
          Loading spec from provider — Smart-fetch can take 5-30s for HTML
          docs (LLM authoring) or under a second for a published OpenAPI
          spec…
        </div>
      )}
      <div style={{
        display: "grid", gap: 8,
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
      }}>
        {templates.map(t => {
          const sourceTag = t.byo_spec ? "BYO spec"
                          : t.spec_url ? "OpenAPI"
                          : t.docs_url ? "from docs"
                          : "—";
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onPick(t)}
              disabled={loading}
              style={{
                textAlign: "left", padding: "12px 14px", borderRadius: 10,
                border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.55 : 1,
                display: "flex", flexDirection: "column", gap: 4,
                minHeight: 86,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
                <span style={{ marginRight: 6 }}>{t.icon}</span>{t.name}
              </div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.4 }}>
                {t.description}
              </div>
              <div style={{
                fontSize: 10, color: COLORS.textDim || COLORS.textMuted,
                marginTop: 2, fontWeight: 600, letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}>
                {sourceTag}
              </div>
            </button>
          );
        })}
        {/* "Start from scratch" — explicit escape hatch into the
            paste-spec / fetch-URL flow. Templates never gate the
            ability to register an arbitrary REST API. */}
        <button
          type="button"
          onClick={onFromScratch}
          disabled={loading}
          style={{
            textAlign: "left", padding: "12px 14px", borderRadius: 10,
            border: `1px dashed ${COLORS.accent}`, background: "transparent",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.55 : 1,
            display: "flex", flexDirection: "column", gap: 4,
            minHeight: 86,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.accentText || COLORS.accent }}>
            ✏️ Start from scratch
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.4 }}>
            Paste any OpenAPI / Swagger spec, or fetch from a docs URL.
          </div>
        </button>
      </div>
    </div>
  );
}


/**
 * Client-side spec → operations enumerator. Mirrors the server's
 * enumerate_operations() output shape ({operationId, method, path,
 * summary}) so when a template's spec is used (no preview-spec round
 * trip), the operations list still renders in Step 2 without an extra
 * server hop.
 */
// Exported for unit testing
export function enumerateOperationsClientSide(spec) {
  if (!spec || !spec.paths) return [];
  const ops = [];
  for (const [path, methods] of Object.entries(spec.paths)) {
    for (const [method, op] of Object.entries(methods)) {
      if (typeof op !== "object" || !op.operationId) continue;
      ops.push({
        operationId: op.operationId,
        method: method.toUpperCase(),
        path,
        summary: op.summary || "",
      });
    }
  }
  return ops;
}


/**
 * OAuth callback banner — rendered after /oauth/callback redirects back
 * with ?oauth=connected|expired|denied|error&detail=...
 *
 * Status semantics (mirror oauth_authcode.py):
 *   connected: token exchange succeeded; refresh_token persisted.
 *   expired:   state TTL elapsed (10 min) OR replay attempt.
 *   denied:    user clicked Deny on the provider's consent screen.
 *   error:     anything else — provider rejected the exchange, missing
 *              params, integration not found, etc.
 */
function OAuthCallbackBanner({ status, onDismiss }) {
  const { status: kind, detail } = status || {};
  const palette = {
    connected: { bg: COLORS.accent, text: "OAuth connection complete — the integration is now active." },
    expired:   { bg: COLORS.yellow || "#eab308", text: "Your sign-in link expired before you finished. Click Reconnect on the integration tile to retry." },
    denied:    { bg: COLORS.textMuted, text: "Sign-in was cancelled. The integration was created but is not yet authorized — click Reconnect on the tile to try again." },
    error:     { bg: COLORS.pink, text: "OAuth couldn't complete. Verify the client_id, client_secret, and redirect URI match what's registered with the provider, then click Reconnect." },
  };
  const info = palette[kind] || palette.error;
  const tint = info.bg;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      padding: "10px 14px", borderRadius: 8,
      background: `${tint}12`, border: `1px solid ${tint}40`,
      marginBottom: 16, fontSize: 12, color: COLORS.text,
    }}>
      <div style={{ flex: 1 }}>
        <strong style={{ color: tint }}>
          {kind === "connected" ? "✓ Connected" : "OAuth notice"}
        </strong>
        {" — "}{info.text}
        {detail && (
          <div style={{ marginTop: 4, color: COLORS.textMuted, fontSize: 11 }}>
            {detail}
          </div>
        )}
      </div>
      <button
        type="button" onClick={onDismiss}
        style={{
          background: "none", border: "none", color: COLORS.textMuted,
          fontSize: 16, lineHeight: 1, cursor: "pointer", padding: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}


function EmptyState({ onAdd }) {
  return (
    <div style={{
      border: `1px dashed ${COLORS.border}`, borderRadius: 12, padding: 32,
      textAlign: "center", color: COLORS.textMuted, fontSize: 13,
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>
        No OpenAPI integrations yet
      </div>
      <div style={{ marginBottom: 16 }}>
        Paste a Swagger/OpenAPI URL — the platform generates MCP tools automatically.
      </div>
      <Button onClick={onAdd}>+ Add your first integration</Button>
    </div>
  );
}


function RefreshDiffModal({ dialog, applying, onCancel, onApply }) {
  // Mid-scope refresh diff modal (Tier-2 #6). Shows added / removed /
  // changed counts, lists the affected operationIds, and highlights
  // operations the tenant currently has enabled that would be impacted.
  const d = dialog?.diff || {};
  const added = d.added || [];
  const removed = d.removed || [];
  const changed = d.changed || [];
  const unchanged = d.unchanged_count || 0;
  const affectedRemoved = d.affected_enabled_operations?.removed || [];
  const affectedChanged = d.affected_enabled_operations?.changed || [];
  const hasAffected = affectedRemoved.length + affectedChanged.length > 0;

  const Section = ({ title, items, color }) => {
    if (!items || items.length === 0) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color, textTransform: "uppercase",
          letterSpacing: "0.06em", marginBottom: 4,
        }}>{title} ({items.length})</div>
        <div style={{
          fontSize: 11, color: COLORS.text, fontFamily: "monospace",
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, padding: "6px 8px", maxHeight: 120, overflow: "auto",
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>{items.join(", ")}</div>
      </div>
    );
  };

  return (
    <Modal
      open={true}
      onClose={onCancel}
      title={`Spec changes — ${dialog.integrationName}`}
      width={620}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button variant="secondary" onClick={onCancel} disabled={applying}>
            Cancel
          </Button>
          <Button onClick={onApply} loading={applying} disabled={applying}>
            {applying ? "Applying…" : "Accept changes"}
          </Button>
        </div>
      }
    >
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 12 }}>
        We re-fetched the spec from its source URL. Review the diff before
        applying — agentgateway's tool list updates immediately after Accept.
      </div>

      {hasAffected && (
        <div style={{
          padding: "10px 12px", marginBottom: 14, borderRadius: 8,
          background: `${COLORS.pink}12`, border: `1px solid ${COLORS.pink}40`,
          fontSize: 12, color: COLORS.text,
        }}>
          <div style={{ fontWeight: 600, color: COLORS.pink, marginBottom: 4 }}>
            ⚠ Operations you have enabled would be affected
          </div>
          {affectedRemoved.length > 0 && (
            <div style={{ marginBottom: 4 }}>
              <strong>Removed by upstream:</strong> {affectedRemoved.join(", ")}
              <span style={{ color: COLORS.textMuted }}> — agents using these tools will break after Accept.</span>
            </div>
          )}
          {affectedChanged.length > 0 && (
            <div>
              <strong>Schema changed:</strong> {affectedChanged.join(", ")}
              <span style={{ color: COLORS.textMuted }}> — review the new shape before agents call them.</span>
            </div>
          )}
        </div>
      )}

      <Section title="Added" items={added} color={COLORS.green || "#10b981"} />
      <Section title="Removed" items={removed} color={COLORS.pink || "#ef4444"} />
      <Section title="Changed" items={changed} color={COLORS.orange || "#f59e0b"} />

      <div style={{
        fontSize: 11, color: COLORS.textMuted, marginTop: 8,
      }}>
        <strong>{unchanged}</strong> operation{unchanged === 1 ? "" : "s"} unchanged.
      </div>
    </Modal>
  );
}


function IntegrationList({ items, onDelete, onRefresh, onTest, refreshLoadingId }) {
  const [busyId, setBusyId] = useState(null);
  const [busyAction, setBusyAction] = useState(null);

  async function run(action, id, fn) {
    setBusyId(id);
    setBusyAction(action);
    try {
      await fn(id);
    } finally {
      setBusyId(null);
      setBusyAction(null);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map(it => {
        const isBusyTest = busyId === it.id && busyAction === "test";
        // Refresh shows loading both during the local `run()` flow AND
        // while the parent's preview round-trip is in flight (so the
        // button stays disabled until the diff modal opens).
        const isBusyRefresh = (busyId === it.id && busyAction === "refresh") || refreshLoadingId === it.id;
        // Item 9: needs_reauth = OAuth refresh failed (provider revoked
        // / rotated). Surface a red status badge + visually-distinct
        // tile border. The Reconnect button below is already wired
        // to drive a fresh /oauth/start.
        const needsReauth = !!it.auth_config?.needs_reauth;
        // M-05: spec_invalid = the reconciler refused to register this
        // integration's target against the SHARED agentgateway because its
        // spec matched a pattern known to crash the gateway's parser (which
        // would take every tenant's OpenAPI tools down). Same visual
        // treatment as needs_reauth — this tile is the "actionable error"
        // the fix's done-when calls for; fixing the spec (Edit → re-paste,
        // or Check for updates against spec_source_url) clears it on the
        // next reconcile pass automatically.
        const specInvalid = !!it.auth_config?.spec_invalid;
        const attentionNeeded = needsReauth || specInvalid;
        return (
          <div key={it.id} style={{
            background: COLORS.surface,
            border: `1px solid ${attentionNeeded ? COLORS.pink : COLORS.border}`,
            borderRadius: 12,
            padding: "14px 18px", display: "flex", alignItems: "center", gap: 14,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{it.name}</div>
                <Badge color={it.enabled ? COLORS.green : COLORS.textMuted}>
                  {it.enabled ? "enabled" : "disabled"}
                </Badge>
                <Badge color={COLORS.accent}>{it.auth_method}</Badge>
                {needsReauth && (
                  <Badge color={COLORS.pink || "#ef4444"}>❗ needs reauth</Badge>
                )}
                {specInvalid && (
                  <Badge color={COLORS.pink}>⚠ spec quarantined</Badge>
                )}
              </div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {it.base_url} · {it.enabled_operations?.length || 0} operations · cred ref: {it.credential_ref}
              </div>
              {needsReauth && it.auth_config?.needs_reauth_reason && (
                <div style={{
                  fontSize: 11, marginTop: 4,
                  color: COLORS.pink || "#ef4444",
                }}>
                  Provider rejected the refresh token. Click <strong>Reconnect</strong> to re-authorize.
                </div>
              )}
              {specInvalid && it.auth_config?.spec_invalid_reason && (
                <div style={{
                  fontSize: 11, marginTop: 4,
                  color: COLORS.pink,
                }}>
                  Not serving tools — spec would break the shared gateway: {it.auth_config.spec_invalid_reason}
                </div>
              )}
            </div>
            {/* /test (canonical-GET upstream check; A8 of cutover plan).
                Disabled when no spec_source_url AND auth_method requires
                a credential — those need /test-credential instead. */}
            <Button
              variant="secondary" size="sm"
              loading={isBusyTest}
              disabled={isBusyTest || isBusyRefresh}
              onClick={() => run("test", it.id, onTest)}
            >Test</Button>
            {/* Tier-2 #6 mid-scope: previews the spec diff first; admin
                explicitly Accepts in a modal before the new spec is
                applied. Only meaningful when the integration was
                created from a spec_source_url. */}
            <Button
              variant="secondary" size="sm"
              loading={isBusyRefresh}
              disabled={!it.spec_source_url || isBusyTest || isBusyRefresh}
              title={it.spec_source_url ? "Check upstream for changes — review the diff before applying" : "Inline-pasted spec — re-paste via Edit, not refresh"}
              onClick={() => run("refresh", it.id, onRefresh)}
            >Check for updates</Button>
            {/* Item 8: Reconnect for OAuth2 authz-code integrations.
                Drives /oauth/start → provider consent → /oauth/callback
                again, refreshing the stored refresh_token. Used after
                a denied/expired OAuth flow OR when the provider
                rotates / revokes the refresh_token (item 9 surfaces
                this state explicitly via auth_config.status). */}
            {it.auth_method === "oauth2_authorization_code" && (
              <Button
                variant="secondary" size="sm"
                disabled={isBusyTest || isBusyRefresh}
                title="Re-run the OAuth consent flow to get a fresh refresh_token"
                onClick={async () => {
                  try {
                    const r = await openapiApi.oauthStart(it.id);
                    window.location.href = r.authorize_url;
                  } catch (e) {
                    alert("OAuth start failed: " + (e?.body?.detail || e?.message || String(e)));
                  }
                }}
              >Reconnect</Button>
            )}
            <Button
              variant="danger" size="sm"
              disabled={isBusyTest || isBusyRefresh}
              onClick={() => onDelete(it.id)}
            >Delete</Button>
          </div>
        );
      })}
    </div>
  );
}


function WizardFooter({ step, onBack, onNext, onPreview, onSubmit, onCancel, previewing, submitting, form }) {
  // Wizard v2 step semantics:
  //   0 = template picker — advances by clicking a tile (no Next btn)
  //   1 = spec source     — advances via "Fetch spec → Next"
  //   2 = operations      — Next when ≥1 op selected
  //   3 = auth + naming   — Next when name/slug/base_url all set; for
  //                         non-none auth we also require either an
  //                         inline credential_value or a pre-existing
  //                         credential_ref (admin pre-seeded path).
  //   4 = review          — Test connection + Create integration
  const isAuthzCode = form.auth_method === "oauth2_authorization_code";
  const canAdvance = computeCanAdvance(step, form);

  // Authz-code → "Create + Connect" makes the two-step flow obvious;
  // other methods → the existing "Create integration" wording.
  const submitLabel = isAuthzCode ? "Create + Connect" : "Create integration";

  return (
    <>
      <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      {step > 0 && <Button variant="secondary" onClick={onBack}>Back</Button>}
      {step === 1 && (
        <Button onClick={onPreview} loading={previewing}>Fetch spec → Next</Button>
      )}
      {(step === 2 || step === 3) && (
        <Button onClick={onNext} disabled={!canAdvance}>Next</Button>
      )}
      {step === 4 && (
        <Button onClick={onSubmit} loading={submitting}>{submitLabel}</Button>
      )}
    </>
  );
}


function Step1SpecSource({ specInput, setSpecInput }) {
  // Wizard v2 — three modes:
  //   "obtain": smart-fetch any URL (OpenAPI 3.x → parsed; Swagger 2.0 →
  //             LLM-converted; HTML docs → LLM-authored). Recommended path
  //             for most users — works for enterprise swagger.json AND
  //             public docs pages alike.
  //   "paste":  inline JSON/YAML when the user already has a known-
  //             good spec.
  // The previous "Strict OpenAPI URL" mode was dropped — Smart-fetch
  // already handles every URL shape (OpenAPI 3 / Swagger 2 / HTML).
  // Having a separate strict-URL mode just gave users a path that
  // crashed on HTML inputs ("spec is neither valid JSON nor YAML").
  const MODES = [
    { key: "obtain", label: "Smart-fetch from URL",
      help: "OpenAPI 3.x / Swagger 2.0 spec OR an HTML docs page — pick one URL." },
    { key: "paste", label: "Paste JSON / YAML",
      help: "Inline a known-good OpenAPI 3.x spec." },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 8 }}>
        {MODES.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setSpecInput({ ...specInput, mode: key })}
            style={{
              flex: 1, padding: "8px 12px", borderRadius: 8, cursor: "pointer",
              background: specInput.mode === key ? COLORS.accent : "transparent",
              color: specInput.mode === key ? "#fff" : COLORS.textMuted,
              border: `1px solid ${specInput.mode === key ? COLORS.accent : COLORS.border}`,
              fontSize: 12, fontWeight: 600,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {specInput.mode === "obtain" && (
        <>
          <Input
            label="Spec or docs URL"
            placeholder="https://api.example.com/swagger.json — or — https://docs.example.com/api"
            value={specInput.url}
            onChange={e => setSpecInput({ ...specInput, url: e.target.value })}
            helper="The platform auto-detects: OpenAPI 3.x → parsed directly; Swagger 2.0 → converted; HTML → spec authored from docs."
          />
          <Input
            label="Hint (optional)"
            placeholder="e.g. 'only stock-data functions, skip forex' or 'Issues API only'"
            value={specInput.hint || ""}
            onChange={e => setSpecInput({ ...specInput, hint: e.target.value })}
            helper="Only used when the URL is an HTML docs page — biases what to include."
          />
        </>
      )}

      {specInput.mode === "paste" && (
        <div>
          <Label>Paste spec (JSON or YAML)</Label>
          <textarea
            value={specInput.text}
            onChange={e => setSpecInput({ ...specInput, text: e.target.value })}
            placeholder='{"openapi": "3.0.0", "info": {"title": "..."}, "paths": {...}}'
            style={{
              width: "100%", minHeight: 220, padding: "10px 12px", borderRadius: 8,
              background: COLORS.surface, color: COLORS.text,
              border: `1px solid ${COLORS.border}`, fontSize: 12, fontFamily: "monospace",
            }}
          />
        </div>
      )}
    </div>
  );
}


function Step2Operations({ operations, selected, onToggle, onSelectAll, onSelectNone }) {
  const [filter, setFilter] = useState("");
  if (operations.length === 0) {
    return (
      <div style={{ color: COLORS.textMuted, fontSize: 12 }}>
        No operations in this spec. (If you didn't run "Fetch spec" on the previous step, go back.)
      </div>
    );
  }
  const OP_CAP = 100;
  const overCap = selected.length > OP_CAP;
  const atCap = selected.length === OP_CAP;
  const counterColor = overCap
    ? (COLORS.red || "#ef4444")
    : atCap
      ? (COLORS.orange || "#f59e0b")
      : COLORS.textMuted;
  // Filter ops by free-text match against operationId / path / summary.
  const fl = filter.trim().toLowerCase();
  const visible = fl
    ? operations.filter(op =>
        (op.operationId || "").toLowerCase().includes(fl)
        || (op.path || "").toLowerCase().includes(fl)
        || (op.summary || "").toLowerCase().includes(fl)
        || (op.method || "").toLowerCase().includes(fl)
      )
    : operations;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: counterColor }}>
            {selected.length} / {OP_CAP} selected
            {selected.length > 0 && operations.length > selected.length && (
              <span style={{ fontWeight: 400, color: COLORS.textMuted }}>
                {" "}(of {operations.length} available)
              </span>
            )}
          </div>
          {overCap && (
            <div style={{ fontSize: 11, color: COLORS.red || "#ef4444", marginTop: 2 }}>
              Over the {OP_CAP}-op cap — backend will reject Create. Deselect some.
            </div>
          )}
          {operations.length > OP_CAP && !overCap && (
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
              {operations.length} ops available — pick up to {OP_CAP}, or split into scoped integrations.
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="sm" variant="secondary" onClick={onSelectAll} title={`Select first ${OP_CAP}`}>
            {operations.length > OP_CAP ? `Select ${OP_CAP}` : "Select all"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onSelectNone}>Clear</Button>
        </div>
      </div>
      <Input
        placeholder="Filter by operationId / path / method / summary…"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        style={{ fontSize: 12 }}
      />
      {fl && (
        <div style={{ fontSize: 11, color: COLORS.textMuted }}>
          {visible.length} match{visible.length !== 1 ? "es" : ""} for "{filter}"
        </div>
      )}
      <div style={{
        maxHeight: 340, overflowY: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 8,
      }}>
        {visible.map(op => {
          const isSelected = selected.includes(op.operationId);
          const wouldExceedCap = !isSelected && selected.length >= OP_CAP;
          return (
            <label key={op.operationId} style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "10px 14px", borderBottom: `1px solid ${COLORS.border}`,
              cursor: wouldExceedCap ? "not-allowed" : "pointer",
              opacity: wouldExceedCap ? 0.45 : 1,
              background: isSelected ? `${COLORS.accent}06` : "transparent",
            }}>
              <input
                type="checkbox"
                checked={isSelected}
                disabled={wouldExceedCap}
                onChange={() => onToggle(op.operationId)}
                style={{ marginTop: 3 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: "flex", alignItems: "center", gap: 8 }}>
                  <Badge color={COLORS.cyan || COLORS.accent}>{op.method}</Badge>
                  <code style={{ fontFamily: "monospace", fontSize: 11 }}>{op.path}</code>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                  {op.operationId}{op.summary ? ` — ${op.summary}` : ""}
                </div>
              </div>
            </label>
          );
        })}
        {visible.length === 0 && (
          <div style={{ padding: "20px 14px", fontSize: 12, color: COLORS.textMuted, textAlign: "center" }}>
            No operations match the filter.
          </div>
        )}
      </div>
    </div>
  );
}


function Step3Auth({ form, setForm }) {
  const fieldHint = AUTH_FIELD_LABELS[form.auth_method];
  const credLabel = form.credential_label || (fieldHint ? fieldHint.label : "Credential");
  const credHelper = form.credential_label_helper ||
    "Stored encrypted in this tenant's settings (Fernet-at-rest).";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Input
        label="Integration name"
        value={form.name}
        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        placeholder="Salesforce CRM"
      />
      <Input
        label="Slug (used in agent tool names)"
        value={form.slug}
        onChange={e => {
          const newSlug = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "");
          // Slug doubles as the leading segment of credential_ref
          // (openapi.<slug>.<field>). Re-derive on slug change so the
          // credential lands at a path that tracks the integration's
          // identity. Without this, two integrations using different
          // slugs would collide on a single credential_ref derived
          // from the template's default slug.
          const conventional = AUTH_FIELD_LABELS[form.auth_method]?.field;
          const nextRef = (newSlug && conventional)
            ? deriveCredentialRef(newSlug, conventional)
            : "";
          setForm(f => ({
            ...f, slug: newSlug,
            credential_ref: nextRef || f.credential_ref,
          }));
        }}
        placeholder="salesforce-crm"
        helper="DNS-1123 compliant: lowercase alnum, dashes, underscores"
      />
      <Input
        label="Base URL"
        value={form.base_url}
        onChange={e => setForm(f => ({ ...f, base_url: e.target.value }))}
        placeholder="https://myorg.my.salesforce.com"
      />
      <Select
        label="Auth method"
        value={form.auth_method}
        onChange={e => {
          const next = e.target.value;
          // Switching auth_method changes the conventional credential
          // field, which changes the auto-derived credential_ref.
          // Reset credential_ref so it re-derives at submit time
          // (unless the user explicitly typed one).
          setForm(f => ({
            ...f, auth_method: next,
            credential_ref: "",
            credential_value: "",
          }));
        }}
      >
        {AUTH_METHODS.map(m => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </Select>
      {form.auth_method === "api_key_header" && (
        <Input
          label="Header name"
          value={form.auth_config?.header_name || ""}
          onChange={e => setForm(f => ({ ...f, auth_config: { ...f.auth_config, header_name: e.target.value } }))}
          placeholder="X-API-Key"
        />
      )}
      {form.auth_method === "api_key_query" && (
        <Input
          label="Query parameter name"
          value={form.auth_config?.query_name || ""}
          onChange={e => setForm(f => ({ ...f, auth_config: { ...f.auth_config, query_name: e.target.value } }))}
          placeholder="api_key"
        />
      )}

      {/* OAuth2 client_credentials + bearer_refresh — same fields:
          token endpoint URL + client_id + optional scopes. The
          long-lived secret (client_secret OR refresh_token) goes in
          the inline credential field below. */}
      {(form.auth_method === "oauth2_client_credentials" || form.auth_method === "bearer_refresh") && (
        <>
          <Input
            label="Token endpoint URL"
            value={form.auth_config?.token_url || ""}
            onChange={e => setForm(f => ({ ...f, auth_config: { ...f.auth_config, token_url: e.target.value } }))}
            placeholder="https://login.example.com/oauth2/token"
            helper="The platform exchanges credentials here on every probe and caches the access_token until it expires."
          />
          <Input
            label={form.auth_method === "oauth2_client_credentials" ? "Client ID" : "Client ID (optional)"}
            value={form.auth_config?.client_id || ""}
            onChange={e => setForm(f => ({ ...f, auth_config: { ...f.auth_config, client_id: e.target.value } }))}
            placeholder="3MVG9…"
          />
          {form.auth_method === "oauth2_client_credentials" && (
            <Input
              label="Scopes (optional)"
              value={form.auth_config?.scopes || ""}
              onChange={e => setForm(f => ({ ...f, auth_config: { ...f.auth_config, scopes: e.target.value } }))}
              placeholder="api read:users"
              helper="Space-separated list."
            />
          )}
        </>
      )}

      {/* OAuth2 authorization code (interactive consent) — RFC 6749
          §4.1 + RFC 7636 (PKCE S256). The wizard collects the
          provider's URLs + client_id; the user-side
          ``Connect`` button on Step 4 drives the /oauth/start ->
          provider consent -> /oauth/callback dance, after which
          tokens land in tenant_settings automatically. The admin
          DOES NOT paste a refresh_token here — it's set by the
          callback. */}
      {form.auth_method === "oauth2_authorization_code" && (
        <>
          <div style={{
            padding: "10px 12px", borderRadius: 8,
            background: `${COLORS.accent}10`,
            border: `1px solid ${COLORS.accent}30`,
            fontSize: 12, color: COLORS.text, lineHeight: 1.45,
          }}>
            <strong>How this works:</strong> after you Create on the next step,
            the wizard opens a popup to the provider's consent screen. Once
            you approve, the platform stores the refresh token and starts
            issuing access tokens automatically.{" "}
            <strong>Register this redirect URI</strong> on the provider
            side first:
            <code style={{
              display: "block", marginTop: 6, padding: "6px 8px",
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              borderRadius: 6, fontSize: 11, wordBreak: "break-all",
            }}>
              {window.location.origin}/api/openapi-integrations/oauth/callback
            </code>
          </div>
          <Input
            label="Authorize URL (provider's /authorize endpoint)"
            value={form.auth_config?.authorize_url || ""}
            onChange={e => setForm(f => ({ ...f, auth_config: { ...f.auth_config, authorize_url: e.target.value } }))}
            placeholder="https://accounts.google.com/o/oauth2/v2/auth"
          />
          <Input
            label="Token URL (provider's /token endpoint)"
            value={form.auth_config?.token_url || ""}
            onChange={e => setForm(f => ({ ...f, auth_config: { ...f.auth_config, token_url: e.target.value } }))}
            placeholder="https://oauth2.googleapis.com/token"
          />
          <Input
            label="Client ID"
            value={form.auth_config?.client_id || ""}
            onChange={e => setForm(f => ({ ...f, auth_config: { ...f.auth_config, client_id: e.target.value } }))}
            placeholder="…apps.googleusercontent.com"
          />
          <Input
            label="Client secret (optional for public clients)"
            type="password"
            value={form.credential_value}
            onChange={e => setForm(f => ({ ...f, credential_value: e.target.value }))}
            placeholder="GOCSPX-…"
            helper="Stored under openapi.<slug>.client_secret (Fernet-encrypted at rest). Public OAuth clients (PKCE-only) can leave this blank."
          />
          <Input
            label="Scopes (space- or comma-separated)"
            value={form.auth_config?.scopes || ""}
            onChange={e => setForm(f => ({ ...f, auth_config: { ...f.auth_config, scopes: e.target.value } }))}
            placeholder="openid email profile"
            helper="What permissions the app is requesting from the user. Provider-specific."
          />
          <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.45 }}>
            <strong>Tip:</strong> for Google, add{" "}
            <code style={{ fontSize: 11 }}>access_type=offline</code> and{" "}
            <code style={{ fontSize: 11 }}>prompt=consent</code> via the
            advanced ``oauth_extra_params`` config (admin API only) to
            force a refresh_token on every grant. For Microsoft, include{" "}
            <code style={{ fontSize: 11 }}>offline_access</code> in scopes.
          </div>
        </>
      )}

      {/* Wizard v2 — inline credential entry. The user pastes the
          actual API key here; on Create, the server saves it under
          openapi.<slug>.<field> via save_settings (encrypted at
          rest). No more "go pre-seed tenant_settings via kubectl"
          ritual. NOT shown for oauth2_authorization_code: that flow
          collects the client_secret inline above (above the scopes
          field) and the refresh_token comes from /oauth/callback. */}
      {form.auth_method !== "none" && form.auth_method !== "oauth2_authorization_code" && (
        <div>
          <Input
            label={credLabel}
            type="password"
            value={form.credential_value}
            onChange={e => setForm(f => ({ ...f, credential_value: e.target.value }))}
            placeholder={form.template_id ? "Paste your secret here…" : "sk-… / xoxb-… / ghp-…"}
            helper={credHelper}
          />
          {form.credential_help_url && (
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
              Need to find this?{" "}
              <a href={form.credential_help_url} target="_blank" rel="noreferrer"
                 style={{ color: COLORS.accent }}>
                {form.credential_help_url}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function Step4Review({ form, operationCount, onTest, testing, testResult }) {
  // Auto-derived credential_ref preview: shown read-only so the admin
  // sees what dotted path their secret is being saved under.
  const conventional = AUTH_FIELD_LABELS[form.auth_method]?.field;
  const derivedRef = form.credential_ref ||
    (conventional && form.slug ? deriveCredentialRef(form.slug, conventional) : "");
  const rows = [
    ["Name", form.name],
    ["Slug", form.slug],
    ["Base URL", form.base_url],
    ["Auth method", form.auth_method],
    ["Credential ref", form.auth_method === "none" ? "(none)" : derivedRef],
    ["Inline credential", form.credential_value ? "✓ supplied (encrypted on save)" : "—"],
    ["Operations exposed", String(operationCount)],
  ];
  // /test-config in v1 supports none/bearer/api_key_header/api_key_query.
  // basic + oauth2_client_credentials still work at runtime via
  // agentgateway but the synthetic probe doesn't speak them yet.
  const testSupported = ["none", "bearer", "api_key_header", "api_key_query"]
    .includes(form.auth_method);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>
        Test the upstream BEFORE saving. Probe is ephemeral — your inline
        credential isn't written to tenant settings until you click Create.
      </div>
      <div style={{
        border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: "hidden",
      }}>
        {rows.map(([k, v], i) => (
          <div key={k} style={{
            display: "flex", padding: "10px 14px",
            borderBottom: i < rows.length - 1 ? `1px solid ${COLORS.border}` : "none",
            fontSize: 12,
          }}>
            <div style={{ width: 160, color: COLORS.textMuted }}>{k}</div>
            <div style={{ flex: 1, color: COLORS.text, fontFamily: k === "Credential ref" ? "monospace" : undefined }}>
              {v || <span style={{ color: COLORS.pink }}>(missing)</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Wizard v2 — Test connection block. */}
      <div style={{
        border: `1px solid ${COLORS.border}`, borderRadius: 8,
        padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 600 }}>
            Test connection
          </div>
          <Button
            size="sm" variant="secondary"
            onClick={onTest} loading={testing}
            disabled={testing || !testSupported}
            title={testSupported ? undefined : `auth_method ${form.auth_method} not yet supported by /test-config — runtime path still works`}
          >
            {testing ? "Probing…" : "Run probe"}
          </Button>
        </div>

        {!testSupported && (
          <div style={{ fontSize: 11, color: COLORS.textMuted }}>
            <em>{form.auth_method}</em> isn't supported by the synthetic
            probe yet. The actual integration will still work at runtime
            via agentgateway — exercise it by sending a chat message
            after Create.
          </div>
        )}

        {testResult && (
          <div style={{
            background: testResult.ok ? `${COLORS.green}10` : `${COLORS.pink}10`,
            border: `1px solid ${testResult.ok ? COLORS.green : COLORS.pink}40`,
            borderRadius: 6, padding: "10px 12px",
            display: "flex", flexDirection: "column", gap: 6,
          }}>
            <div style={{ fontSize: 12, fontWeight: 600,
                          color: testResult.ok ? COLORS.green : COLORS.pink }}>
              {testResult.ok ? "✓" : "✗"} {testResult.method || "GET"}{" "}
              {testResult.path || ""} → HTTP {testResult.status}
              {testResult.error_type && ` (${testResult.error_type})`}
              {testResult.latency_ms != null && ` · ${testResult.latency_ms}ms`}
            </div>
            {testResult.body_excerpt && (
              <div style={{
                fontSize: 11, fontFamily: "monospace", color: COLORS.textMuted,
                whiteSpace: "pre-wrap", overflowWrap: "anywhere",
                maxHeight: 120, overflowY: "auto",
              }}>
                {testResult.body_excerpt.slice(0, 500)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
